import type { IntegrationResource, ProjectUsageByCreator } from '@common/api/brandGroupService';

export type BrandConnectionStatus = 'connected' | 'needs-reauthorization' | 'sync-issue';
export type BrandRole = 'VIEW' | 'EDIT' | 'OWNER';
export type BrandResource = IntegrationResource & {
  brandIds: string[];
  /** Maximum permission delegated by this Project resource assignment, keyed by Project id. */
  brandAccessCeilings?: Record<string, 'DISABLED' | 'READ' | 'WRITE'>;
  /** Labels are keyed by Business group because one app account can vary by group. */
  brandLabels?: Record<string, string>;
};
export type BrandConnection = {
  id: string;
  provider: string;
  accountName: string;
  accountDetail: string;
  brandId: string | null;
  brandIds: string[];
  /** Maximum permission delegated by this Project assignment, keyed by Project id. */
  brandAccessCeilings?: Record<string, 'DISABLED' | 'READ' | 'WRITE'>;
  status: BrandConnectionStatus;
  /** Server-provided display name for the connector after owner identities are redacted. */
  connectedByName?: string;
  connectedByUserId: string;
  connectedAt: string;
  assignedByUserId: string | null;
  assignedAt: string | null;
  /** Whether the current user can use this Workspace integration. */
  canUseConnection?: boolean;
  canManageConnection?: boolean;
  canManageAccess?: boolean;
  resources: BrandResource[];
};

export type BrandUser = {
  id: string;
  name: string;
  email: string;
  role: 'Owner' | 'Admin' | 'Member';
};

export type BrandMembership = {
  brandId: string;
  userId: string;
  role: Exclude<BrandRole, 'OWNER'>;
  status?: 'ACTIVE' | 'INACTIVE';
};

export type BrandRecord = {
  id: string;
  isDefault?: boolean;
  scope?: 'workspace' | 'private';
  visibility?: 'WORKSPACE' | 'PRIVATE';
  name: string;
  domain: string;
  market: string;
  description: string;
  logoUrl: string | null;
  accent: string;
  createdByUserId?: string;
  primaryOwnerUserId?: string;
  memberRole?: 'VIEW' | 'EDIT' | null;
};

export type BrandGroupData = {
  /** Workspace-wide active Project count; may exceed the caller's visible list. */
  totalBrandCount: number;
  /** Workspace-wide aggregate counts; contains no private Project identity. */
  projectUsageByCreator: ProjectUsageByCreator[];
  brands: BrandRecord[];
  /** Active Projects available as integration-assignment targets. */
  assignmentBrands: BrandRecord[];
  connections: BrandConnection[];
  users: BrandUser[];
  memberships: BrandMembership[];
};

/** The backend-owned Business-group name is the display name in every surface. */
export const getBusinessGroupDisplayName = (
  businessGroup: Pick<BrandRecord, 'name'>,
): string => businessGroup.name;

export const getBrandConnectionDisplayName = ({
  provider,
  accountName,
  accountDetail,
}: Pick<BrandConnection, 'provider' | 'accountName' | 'accountDetail'>): string => {
  const normalizedProvider = provider.trim().toLowerCase();
  if (normalizedProvider === 'shopify') {
    return accountName || accountDetail;
  }

  if (
    normalizedProvider === 'google ads' ||
    normalizedProvider === 'search console' ||
    normalizedProvider === 'google search console'
  ) {
    return accountName || accountDetail;
  }

  return accountDetail || accountName;
};

export const emptyBrandGroupData = (): BrandGroupData => ({
  totalBrandCount: 0,
  projectUsageByCreator: [],
  brands: [],
  assignmentBrands: [],
  connections: [],
  users: [],
  memberships: [],
});

export const getBrandConnections = (data: BrandGroupData, brandId: string): BrandConnection[] =>
  data.connections.filter((connection) => connection.brandIds.includes(brandId));

export const getBrandMembership = (data: BrandGroupData, brandId: string, userId: string): BrandMembership | null =>
  data.memberships.find((membership) => membership.brandId === brandId && membership.userId === userId) || null;

export const getBrandMemberRole = (data: BrandGroupData, brandId: string, userId: string): BrandRole | null => {
  const user = data.users.find((candidate) => candidate.id === userId);
  if (user?.role === 'Owner') return 'OWNER';
  return getBrandMembership(data, brandId, userId)?.role || null;
};

export const getBrandUsers = (data: BrandGroupData, brandId: string): BrandUser[] =>
  data.users.filter((user) => user.role === 'Owner' || Boolean(getBrandMembership(data, brandId, user.id)));

export const getBrandResources = (data: BrandGroupData, brandId: string): BrandResource[] =>
  data.connections.flatMap((connection) =>
    connection.resources.filter((resource) => resource.brandIds.includes(brandId)),
  );
